import React from 'react'
import { useQuery } from '@apollo/client/react'
import {
  Accordion,
  AccordionSummary,
  FormControlLabel,
  List,
  ListItem,
  Radio,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { GET_SUBSCRIPTION_PLAN } from '@/housefolios-components/CreateAccount/CreateAccountCard/queries'
import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'

const numberFormat = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

export default function OrgPlanAccordion({
  plan,
  orgType,
  billing,
  selectedPlan,
  setSelectedPlan,
  setSubscriptionPlan,
  setPlanPrice,
  setPlanBilling,
  currencyFormat,
}) {
  const subscriptionPlanId =
    billing === 'monthly'
      ? orgType === 'Partner Organization'
        ? plan.nameMonthlyPartner
        : plan.nameMonthly
      : plan.nameYearly

  const { loading, error, data } = useQuery(GET_SUBSCRIPTION_PLAN, {
    variables: { subscriptionPlan: subscriptionPlanId },
  })

  if (loading) return <LoadingSkeleton variant="accordion" />
  if (error) return `Error! ${error.message}`

  const { subscriptionPlan } = data
  const planName =
    billing === 'monthly' ? plan.nameMonthly : plan.nameYearly

  const priceLabel =
    billing === 'monthly' ? (
      <>
        {plan.label} -{' '}
        {(plan.personalizedCoaching || plan.groupCoaching) &&
          `1x ${plan.personalizedCoaching ? 'Personal' : 'Group'} Coaching Fee then`}{' '}
        {currencyFormat.format(subscriptionPlan.price / 100)}
        /mo
      </>
    ) : (
      <>
        {plan.label} -{' '}
        {currencyFormat.format(subscriptionPlan.price / 100)}
        /yr
      </>
    )

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-label="Expand"
        aria-controls="additional-actions1-content"
        id="additional-actions1-header"
      >
        <FormControlLabel
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <Radio
              checked={selectedPlan === subscriptionPlan.planId}
              value={subscriptionPlan.planId}
              onChange={(event) => {
                setSelectedPlan(event.target.value)
                setSubscriptionPlan(planName)
                setPlanPrice(subscriptionPlan.price / 100)
                setPlanBilling(billing)
              }}

              sx={{ mr: '0.5rem' }}
            />
          }
          label={
            <Typography
              style={{
                fontWeight: 600,
              }}
            >
              {priceLabel}
            </Typography>
          }
        />
      </AccordionSummary>
      <List>
        {billing === 'monthly' && plan.personalizedCoaching && (
          <ListItem>
            <Typography color="textSecondary">
              Monthly One on One Coaching
            </Typography>
          </ListItem>
        )}
        {billing === 'monthly' && plan.groupCoaching && (
          <ListItem>
            <Typography color="textSecondary">
              Weekly Group Coaching
            </Typography>
          </ListItem>
        )}
        {plan.label !== 'Free' ? (
          <ListItem>
            <Typography color="textSecondary">
              Analyze{' '}
              <b>
                {numberFormat.format(subscriptionPlan.propertyCount)}
              </b>{' '}
              Properties/mo
            </Typography>
          </ListItem>
        ) : (
          <ListItem>
            <Typography color="textSecondary">
              *Analyze <b>{plan.properties}</b> Properties/mo
            </Typography>
          </ListItem>
        )}
        <ListItem>
          <Typography color="textSecondary">
            Create up to <b>{subscriptionPlan.porfolioCount || 1}</b>{' '}
            portfolio
          </Typography>
        </ListItem>
        <ListItem>
          <Typography color="textSecondary">
            Add <b>{subscriptionPlan.teamCount || 0}</b> team members
          </Typography>
        </ListItem>
        <ListItem>
          <Typography color="textSecondary">{plan.search}</Typography>
        </ListItem>
        <ListItem>
          <Typography color="textSecondary">
            {plan.analyze}
          </Typography>
        </ListItem>
        <ListItem>
          <Typography color="textSecondary">{plan.comp}</Typography>
        </ListItem>
        {plan.housefoliosMarketplace && (
          <ListItem>
            <Typography color="textSecondary">
              {plan.housefoliosMarketplace}
            </Typography>
          </ListItem>
        )}
        {subscriptionPlan.marketplaceCount > 0 &&
          subscriptionPlan.marketplaceCount && (
            <ListItem>
              <Typography color="textSecondary">
                Create <b>{subscriptionPlan.marketplaceCount}</b>{' '}
                Marketplace websites
              </Typography>
            </ListItem>
          )}
        {orgType === 'Partner Organization' &&
          plan.partnerWarning && (
            <ListItem>
              <Typography color="textSecondary">
                {plan.partnerWarning}
              </Typography>
            </ListItem>
          )}
      </List>
    </Accordion>
  )
}
